/**
 * Administration → Location Master
 * Shows India Location Master coverage. Read-only in this version; ready for
 * authorised sync when LGD / India Post Directory import is enabled.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Search, Info } from "lucide-react";

export default function LocationMaster() {
  const [status, setStatus] = useState(null);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("TN");
  const [districts, setDistricts] = useState([]);
  const [pin, setPin] = useState("600040");
  const [pinMatches, setPinMatches] = useState(null);

  useEffect(() => {
    api.get("/locations/master/status").then(({ data }) => setStatus(data));
    api.get("/locations/india/states").then(({ data }) => setStates(data.items || []));
  }, []);

  useEffect(() => {
    if (!selectedState) return;
    api.get("/locations/india/districts", { params: { state: selectedState } })
      .then(({ data }) => setDistricts(data.items || []));
  }, [selectedState]);

  const lookupPin = async () => {
    setPinMatches(null);
    try {
      const { data } = await api.get(`/locations/india/pin/${pin}`);
      setPinMatches(data);
    } catch (e) {
      setPinMatches({ pin, matches: [], notice: e.response?.data?.detail || "Lookup failed." });
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="location-master">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Administration</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Location Master</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Structured geographic and postal data used across shipments, pickups, deliveries and employee addresses.
          India is enabled today; the schema is country-agnostic and ready for LGD + India Post Directory imports.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Tile label="States"    value={status?.states ?? "—"} />
        <Tile label="Districts" value={status?.districts ?? "—"} />
        <Tile label="Cities"    value={status?.cities ?? "—"} />
        <Tile label="PIN codes" value={status?.pins ?? "—"} />
        <Tile label="Source"    value={status?.data_source ? "Seeded" : "—"} small />
      </div>

      <section className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-800 font-medium">
          <Search className="h-4 w-4 text-slate-500" /> PIN lookup
        </div>
        <div className="p-4 flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[180px]">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Enter 6-digit PIN</div>
            <input className="input" data-testid="pin-input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="600040" />
          </label>
          <button data-testid="pin-lookup" onClick={lookupPin} className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800">Look up</button>
        </div>
        {pinMatches && (
          <div className="px-4 pb-4">
            {pinMatches.matches?.length ? (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                {pinMatches.matches.map((m, i) => (
                  <li key={i} className="p-3 text-sm text-slate-800">
                    <div className="font-medium">{m.post_office} <span className="text-slate-400 font-normal">· {m.locality}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{m.city} · {m.district} · {m.state_code} · PIN {m.pin}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5" /> {pinMatches.notice || "No matches found."}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-800 font-medium">
          <MapPin className="h-4 w-4 text-slate-500" /> India · States → Districts
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-x divide-slate-100">
          <div className="max-h-[420px] overflow-y-auto">
            {states.map((s) => (
              <button
                key={s.state_code}
                onClick={() => setSelectedState(s.state_code)}
                data-testid={`state-${s.state_code}`}
                className={
                  "w-full text-left px-4 py-2 text-sm border-b border-slate-50 " +
                  (selectedState === s.state_code
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-50")
                }
              >
                {s.name} <span className={selectedState === s.state_code ? "text-white/60" : "text-slate-400"}>({s.state_code})</span>
              </button>
            ))}
          </div>
          <div className="md:col-span-2 max-h-[420px] overflow-y-auto">
            {districts.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">No districts seeded for this state yet. LGD import will populate.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {districts.map((d) => (
                  <li key={d.name} className="p-3 text-sm text-slate-800">{d.name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <p className="text-[11px] text-slate-500">
        Historical shipments and pickups retain their original address values regardless of future changes to this master.
        Renamed or merged administrative entities are marked <span className="font-medium">Renamed / Merged / Inactive</span> — never deleted.
      </p>
    </div>
  );
}

function Tile({ label, value, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
      <div className={"mt-1 font-heading font-semibold text-slate-900 " + (small ? "text-sm" : "text-2xl tracking-tighter tabular-nums")}>{value}</div>
    </div>
  );
}
